import { createObOrderSet, fetchObOrderSets, updateObOrderSet } from '@/src/api/antenatal'
import type { ObOrderSet } from '@type/antenatal.type'

const STORAGE_KEY = 'ob-order-sets'
const MIGRATED_KEY = 'ob-order-sets-migrated'

function readLocal(): ObOrderSet[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as ObOrderSet[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function clearLocal() {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.setItem(MIGRATED_KEY, '1')
}

/** One-time: push browser-saved sets with items into the DB, then clear localStorage. */
async function migrateLocalIfNeeded(serverSets: ObOrderSet[]) {
  if (localStorage.getItem(MIGRATED_KEY) === '1') return serverSets
  const local = readLocal().filter(set => set.attributes.items.length > 0)
  if (local.length === 0) {
    localStorage.setItem(MIGRATED_KEY, '1')
    return serverSets
  }
  const created: ObOrderSet[] = []
  for (const set of local) {
    try {
      created.push(await createObOrderSet(set))
    } catch {
      // Leave local data so the clinic can retry after the API is available.
      return serverSets
    }
  }
  clearLocal()
  return [...serverSets, ...created]
}

export async function loadOrderSets(): Promise<ObOrderSet[]> {
  const sets = await fetchObOrderSets()
  return migrateLocalIfNeeded(sets)
}

export async function persistOrderSet(set: ObOrderSet) {
  if (set.id.startsWith('local-')) {
    return createObOrderSet(set)
  }
  return updateObOrderSet(set)
}
